/**
 * Import React libraries.
 */
import * as React from 'react';

/**
 * Import custom components.
 */
import { InputChip } from './index';

/**
 * Import custom styles.
 */
import { ChipInputContainer, ChipInputEl, RequiredStar } from './ChipStyles';

/**
 * Import custom types.
 */
import { ChipInputProps } from './ChipTypes';

const ChipInput: React.FunctionComponent<ChipInputProps> = ({ values, inputId, variants, required, icon, placeholder, onValueChange }: ChipInputProps) => {
  // Globally used variables within the chip input
  let chipInput: HTMLInputElement;
  const [chips, setChips] = React.useState<string[]>([]);
  const [value, setValue] = React.useState<string>('');

  /**
   * Sets initial values for the chips.
   */
  React.useEffect(() => {
    // Let's add initial chip values
    setChips(values);
  }, [values]);

  /**
   * Adds a chip to the chip list.
   * @param keyCode - Key code which was triggered.
   */
  const onAddChip = (keyCode: number): void => {
    // Let's trigger only on enter press
    if (keyCode === 13 && value) {
      // Let's add the chip
      setChips([...chips, value]);
      onValueChange([...chips, value]);

      // Let's clear the old value
      chipInput.value = '';
      setValue('');
    }
  }

  /**
   * Removes a specific chip from the chip list.
   * @param index - Index of the chip that needs to be removed.
   * @param elementToAdd - Element which needs to be added in the deleted element place.
   */
  const onRemoveChip = (index: number, elementToAdd?: string): void => {
    // Let's copy the chips
    const newChips = [...chips];

    // Let's delete the chip
    // If necessary, let's replace it with a different chip
    if (elementToAdd) {
      newChips.splice(index, 1, elementToAdd);
    } else {
      newChips.splice(index, 1);
    }

    // Let's update the chip list
    setChips([...newChips]);
    onValueChange([...newChips]);
  }

  /**
   * Changes a status of the chip to edit.
   * @param text - Text of the chip that is being edited.
   * @param index - Index of the chip in the array.
   */
  const onEditChip = (text: string, index: number): void => {
    // Let's check if we have to add value as a chip (in case we have entered something in the input field)
    if (chipInput.value !== '') {
      // Let's replace the chip
      onRemoveChip(index, chipInput.value);
    } else {
      // Let's just remove the chip
      onRemoveChip(index);
    }

    // Let's update the text value of input field
    chipInput.value = text;
    setValue(text);
  }

  /**
   * Return Chip Input component.
   */
  return (
    <>
      {required && <RequiredStar>*</RequiredStar>}
      <ChipInputContainer onClick={() => chipInput.focus() } id={inputId}>
        {
          chips.map((chip: string, index: number) => (
            <InputChip icon={icon} size="medium" variant={variants && variants.length && variants[index] ? variants[index] : 'normal' } text={chip} onRemove={() => onRemoveChip(index)}
              onClick={() => onEditChip(chip, index)} key={`chip_${index}`}></InputChip>
          ))
        }
        <ChipInputEl type="text" onChange={(event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value)}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => onAddChip(event.keyCode)}
          onBlur={() => onAddChip(13) /* Let's simulate enter key */ }
          placeholder={placeholder && chips.length === 0 ? placeholder : ''}
          ref={(input: HTMLInputElement) => { chipInput = input; }}  />
      </ChipInputContainer>
    </>
  );
};

export default ChipInput;
